import { PageLoader } from "@kamae-apps/shared/Components/Atoms/PageLoader/PageLoader"
import useApiStatus from "@kamae-apps/shared/Hooks/useApiStatus/useApiStatus"
import { UserConfigStateProvider } from "@kamae-apps/shared/Hooks/userConfig/Context"
import { COMPANY_STATUS } from "@kamae-apps/shared/Types/Company"
import { SCOPE, scopeHas } from "@kamae-apps/shared/Types/Scope"
import React, { FC, Suspense } from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, Route, Routes } from "react-router-dom"
import { AppLayout } from "./Components/Layout/AppLayout"
import AppLoader from "./Components/Organisms/Loaders/AppLoader/AppLoader"
import { useUser } from "./Contexts/User/UserContext"
import Error404Page from "./Routes/Error404/Error404Page"
import AppLayoutGuard from "./Routes/Guards/AppLayoutGuard"
import UserGuard from "./Routes/Guards/UserGuard"
import LearningPage from "./Routes/Learning/LearningPage"
import MaintenancePage from "./Routes/Maintenance/MaintenancePage"
import { useAdministrationRedirectTarget } from "./hooks/useAdministrationRedirectTarget"

const LoginPage = React.lazy(() => import("./Routes/Login/LoginPage"))
const LegacyOrganizationPage = React.lazy(() => import("./Routes/Organization/Legacy/LegacyOrganizationPage"))
const OrganizationToolsPage = React.lazy(() => import("./Routes/Organization/Tools/ToolsPage"))
const CollaboratorPage = React.lazy(() => import("./Routes/Administration/Collaborator/CollaboratorPage"))
const TeamsPage = React.lazy(() => import("./Routes/Administration/Teams/TeamsPage"))
const PopulationPage = React.lazy(() => import("./Routes/Administration/Population/PopulationPage"))
const SkillsPage = React.lazy(() => import("./Routes/Administration/Skills/SkillsPage"))
const SkillDetailPage = React.lazy(() => import("./Routes/Administration/Skills/SkillDetailPage/SkillDetailPage"))
const AwarenessProgramPage = React.lazy(() => import("./Routes/Administration/AwarenessProgram/AwarenessProgramPage"))
const PopulationDetailPage = React.lazy(
  () => import("./Routes/Administration/Population/PopulationDetailPage/PopulationDetailPage")
)
const WelcomePage = React.lazy(() => import("./Routes/Welcome/WelcomePage"))
const AvatarsPage = React.lazy(() => import("./Routes/Avatars/AvatarsPage"))
const HomePage = React.lazy(() => import("./Routes/Home/HomePage"))
const PhishingCreatePage = React.lazy(() => import("./Routes/Phishing/Create/PhishingCreatePage"))
const LibraryPage = React.lazy(() => import("./Routes/Library/LibraryPage"))
const AutomationPage = React.lazy(() => import("./Routes/Phishing/Automation/AutomationPage"))
const PhishingPageRouter = React.lazy(() =>
  import("./Routes/Phishing/PhishingRouter").then(module => ({ default: module.PhishingPageRouter }))
)
const PhishingDetailPageRouter = React.lazy(() =>
  import("./Routes/Phishing/PhishingRouter").then(module => ({ default: module.PhishingDetailPageRouter }))
)
const PhishingAdminCampaignResultPage = React.lazy(
  () => import("./Routes/Phishing/Admin/PhishingAdminCampaignResultPage/PhishingAdminCampaignResultPage")
)
const BotPage = React.lazy(() => import("./Routes/Bot/BotPage"))
const ChallengePageLegacy = React.lazy(() => import("./Routes/Challenge/legacy/ChallengePageLegacy"))
const ChallengePage = React.lazy(() => import("./Routes/Challenge/ChallengePage"))
const DashboardPage = React.lazy(() => import("./Routes/Dashboard/DashboardPage"))
const TatamiPage = React.lazy(() => import("./Routes/Tatami/TatamiPage"))
const DojoPage = React.lazy(() => import("./Routes/Dojo/DojoPage"))
const AdministrationPage = React.lazy(() => import("./Routes/Administration/AdministrationPage"))
const SpecialEventPage = React.lazy(() => import("./Routes/SpecialEvent/SpecialEventPage"))

type AppRoutesProps = {
  isUserLoaded: boolean
  featureFlag: string[]
  displayNewBeltModal: boolean
  setDisplayNewBeltModal: React.Dispatch<React.SetStateAction<boolean>>
}

export const AppRoutes: FC<AppRoutesProps> = ({
  isUserLoaded,
  featureFlag,
  displayNewBeltModal,
  setDisplayNewBeltModal,
}) => {
  const apiStatus = useApiStatus()
  const { t } = useTranslation("app")
  const { user } = useUser()

  const displayRankings = !user.company.hide_rankings

  const intercom = document.querySelector(".intercom-lightweight-app-launcher.intercom-launcher")

  if (intercom) {
    // Workaround before we modify the intercom component inside the app settings //
    intercom.setAttribute("style", "background: #056EF7")
  }

  return (
    <div className={"bg-gradient-ocean bg-no-repeat bg-fixed overflow-hidden"}>
      {((apiStatus.code === 503 || apiStatus.code === -1) && <MaintenancePage />) || (
        <Routes>
          <Route
            path={"/login"}
            element={
              <Suspense fallback={<AppLoader />}>
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            element={
              <UserGuard isUserLoaded={isUserLoaded}>
                <UserConfigStateProvider loaded={isUserLoaded}>
                  <AppLayoutGuard>
                    <Outlet />
                  </AppLayoutGuard>
                </UserConfigStateProvider>
              </UserGuard>
            }
          >
            <>
              {typeof user.avatar === "undefined" && (
                <Route
                  path={"/welcome"}
                  element={
                    <Suspense fallback={<AppLoader />}>
                      <WelcomePage />
                    </Suspense>
                  }
                />
              )}
              <Route
                path={"/new-challenge"}
                element={
                  <Suspense fallback={<AppLoader />}>
                    <ChallengePage />
                  </Suspense>
                }
              />
              <Route
                path={"/special-event"}
                element={
                  <Suspense fallback={<AppLoader />}>
                    <SpecialEventPage />
                  </Suspense>
                }
              />
            </>
          </Route>
          <Route
            element={
              <UserGuard isUserLoaded={isUserLoaded}>
                <UserConfigStateProvider loaded={isUserLoaded}>
                  <AppLayoutGuard>
                    <AppLayout
                      t={t}
                      isUserLoaded={isUserLoaded}
                      displayNewBeltModal={displayNewBeltModal}
                      setDisplayNewBeltModal={setDisplayNewBeltModal}
                    >
                      <Outlet />
                    </AppLayout>
                  </AppLayoutGuard>
                </UserConfigStateProvider>
              </UserGuard>
            }
          >
            {scopeHas(user?.scope, SCOPE.COMPANY_ADMIN) && (
              <>
                <Route
                  path={"/organization/*"}
                  element={
                    <Navigate
                      to={useAdministrationRedirectTarget()}
                      replace
                    />
                  }
                />
                <Route
                  path="/administration"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AdministrationPage />
                    </Suspense>
                  }
                >
                  <Route
                    index
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <LegacyOrganizationPage hideHeaderBar />
                      </Suspense>
                    }
                  />
                  <Route
                    path={"users"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <CollaboratorPage company={user.company} />
                      </Suspense>
                    }
                  />
                  <Route
                    path={"teams"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <TeamsPage company={user.company} />
                      </Suspense>
                    }
                  />
                  <Route
                    path={"population"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <PopulationPage />
                      </Suspense>
                    }
                  />
                  <Route
                    path={"population/:id"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <PopulationDetailPage />
                      </Suspense>
                    }
                  />
                  {featureFlag.includes("skills-submenu") && (
                    <>
                      <Route
                        path={"skills"}
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <SkillsPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path={"skills/:id"}
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <SkillDetailPage />
                          </Suspense>
                        }
                      />
                    </>
                  )}
                  {featureFlag.includes("awareness-program-submenu") && (
                    <Route
                      path={"awareness-program"}
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AwarenessProgramPage />
                        </Suspense>
                      }
                    />
                  )}
                  <Route
                    path={"tools"}
                    element={
                      <Suspense fallback={<PageLoader />}>
                        <OrganizationToolsPage hideBackLink />
                      </Suspense>
                    }
                  />
                </Route>
              </>
            )}
            {(scopeHas(user.scope, SCOPE.COMPANY_ADMIN) || user.id === user.team.leader?.id) && (
              <Route
                path={"/dashboard"}
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DashboardPage />
                  </Suspense>
                }
              />
            )}
            <Route
              path={"/home"}
              element={
                <Suspense fallback={<PageLoader />}>
                  <HomePage />
                </Suspense>
              }
            />
            <Route
              path={"/elearning"}
              element={
                <Navigate
                  to="/tatami#trainings"
                  replace
                />
              }
            />
            <Route
              path={"/tatami"}
              element={
                <Suspense fallback={<PageLoader />}>
                  <TatamiPage />
                </Suspense>
              }
            />
            {displayRankings && (
              <Route
                path={"/dojo"}
                element={
                  <Suspense fallback={<PageLoader />}>
                    <DojoPage />
                  </Suspense>
                }
              />
            )}
            {user.company.phishing_enabled && (
              <Route path={"/phishing"}>
                <Route
                  index
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PhishingPageRouter />
                    </Suspense>
                  }
                />
                <Route
                  path={":id"}
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PhishingDetailPageRouter />
                    </Suspense>
                  }
                />
                <Route
                  path={":campaignId/user/:userId"}
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PhishingAdminCampaignResultPage />
                    </Suspense>
                  }
                />
                {scopeHas(user?.scope, SCOPE.COMPANY_ADMIN) &&
                  !featureFlag.includes("phishing-attack-creation-disabled") && (
                    <Route
                      path={"create"}
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PhishingCreatePage />
                        </Suspense>
                      }
                    />
                  )}
                {scopeHas(user?.scope, SCOPE.COMPANY_ADMIN) &&
                  user.company.status !== COMPANY_STATUS.STARTER &&
                  user.company.status !== COMPANY_STATUS.TRIAL && (
                    <Route
                      path={"automation"}
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AutomationPage />
                        </Suspense>
                      }
                    />
                  )}
              </Route>
            )}
            <Route
              path={"/reflexes"}
              element={
                <Suspense fallback={<PageLoader />}>
                  <LibraryPage />
                </Suspense>
              }
            />
            <Route
              path={"/profile"}
              element={
                <Suspense fallback={<PageLoader />}>
                  <AvatarsPage />
                </Suspense>
              }
            />
            {user.company.status === COMPANY_STATUS.STARTER && (
              <Route
                path={"/challenge"}
                element={
                  <Suspense fallback={<PageLoader />}>
                    <ChallengePageLegacy />
                  </Suspense>
                }
              />
            )}
            {/* TODO(KAM-3218): legacy redirect for old /supervision bookmarks — remove after 2026-08-11 */}
            <Route
              path={"/supervision/*"}
              element={
                <Navigate
                  to={"/dashboard"}
                  replace
                />
              }
            />
            <Route
              path={"*"}
              element={<Error404Page />}
            />
          </Route>
          <Route
            path={"/bot"}
            element={<BotPage />}
          />
          <Route
            path={"/learning-page"}
            element={<LearningPage />}
          />
        </Routes>
      )}
    </div>
  )
}
